import { type MouseEventHandler, type PropsWithChildren, useMemo } from 'react'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'

import { GridDurationRow } from './grid-duration-row'
import {
	calculateTimeFromY,
	STEP_TIME_MINUTES,
	snapToGrid,
	type TimeslotBounds,
	TOTAL_HEIGHT,
} from './helpers'
import type { BookingOption } from '../step-time'

const { getTimeAsMinutes } = dateTimeUtils.utils

export const DraggableTimeslotContainer = ({
	calendar,
	bounds,
	children,
	value,
	onTimeChange,
}: PropsWithChildren<{
	calendar: { available: boolean; hour: string }[]
	bounds: TimeslotBounds
	value: BookingOption
	onTimeChange: (start: string, end: string) => void
}>) => {
	const handleContainerClick: MouseEventHandler<HTMLDivElement> = (e) => {
		const containerRect = e.currentTarget.getBoundingClientRect()
		const clickY = e.clientY - containerRect.top

		let newStartY = snapToGrid(clickY - bounds.height / 2)

		newStartY = Math.max(0, Math.min(newStartY, TOTAL_HEIGHT - bounds.height))

		onTimeChange(
			calculateTimeFromY(newStartY),
			calculateTimeFromY(newStartY + bounds.height),
		)
	}

	const grid = useMemo(() => {
		const start = getTimeAsMinutes(searchConfig.timeSlot.min)
		const end = getTimeAsMinutes(searchConfig.timeSlot.max)
		const duration = end - start

		return Array.from({ length: duration / STEP_TIME_MINUTES + 1 }).map(
			(_, i) => {
				const hour = dateTimeUtils
					.duration(start + i * STEP_TIME_MINUTES, 'minutes')
					.format('HH[:]mm')

				return {
					available:
						calendar?.find((timeslot) => timeslot.hour === hour)?.available ||
						false,
					hour,
				}
			},
		)
	}, [calendar])

	return (
		<div
			className='relative ml-[40px] cursor-pointer'
			onClick={handleContainerClick}
			style={{ height: TOTAL_HEIGHT }}
		>
			{grid.map((row) => (
				<GridDurationRow
					isAvailable={row.available}
					key={row.hour}
					rowTime={row.hour}
					value={value}
				/>
			))}

			{children}
		</div>
	)
}
